import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminLogin from './components/AdminLogin';
import DocEditor from './components/DocEditor';
import TextEditor from './components/TextEditor';
import { documentsData, patriotDocumentsData } from '@/mocks/documents';
import type { DocItem } from '@/mocks/documents';

const DOCS_KEY = 'admin_docs';
const PATRIOT_KEY = 'admin_patriot_docs';

function loadDocs(key: string, fallback: DocItem[]): DocItem[] {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

type Tab = 'docs' | 'patriot' | 'texts';

export default function AdminPage() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem('admin_auth') === '1');
  const [tab, setTab] = useState<Tab>('docs');
  const [docs, setDocs] = useState<DocItem[]>(() => loadDocs(DOCS_KEY, documentsData));
  const [patriotDocs, setPatriotDocs] = useState<DocItem[]>(() => loadDocs(PATRIOT_KEY, patriotDocumentsData));
  const { i18n } = useTranslation();
  const isKk = (i18n.language || 'kk').startsWith('kk');

  const handleLogout = () => {
    sessionStorage.removeItem('admin_auth');
    setAuthed(false);
  };

  useEffect(() => {
    document.title = isKk
      ? 'Әкімшілік — Әскери-қолданбалы спорт'
      : 'Администрация — Военно-прикладной спорт';
  }, [isKk]);

  if (!authed) {
    return <AdminLogin onSuccess={() => setAuthed(true)} />;
  }

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: 'docs', label: isKk ? 'Құжаттар' : 'Документы', icon: 'ri-file-list-3-line' },
    { key: 'patriot', label: isKk ? 'Patriot құжаттары' : 'Документы Patriot', icon: 'ri-shield-star-line' },
    { key: 'texts', label: isKk ? 'Сайт мәтіндері' : 'Тексты сайта', icon: 'ri-text' },
  ];

  return (
    <div className="min-h-screen bg-background-950">
      {/* Top bar */}
      <header className="w-full bg-background-900/95 backdrop-blur-md border-b border-background-800/50 sticky top-0 z-40">
        <div className="w-full px-4 md:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center">
              <img
                src="/favicon.png"
                alt="Логотип"
                className="w-full h-full object-contain filter invert"
              />
            </div>
            <span className="font-heading text-foreground-200 text-sm tracking-widest uppercase">
              {isKk ? 'Әкімшілік панелі' : 'Панель администратора'}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 text-xs text-foreground-500 hover:text-foreground-300 font-label transition-colors whitespace-nowrap"
            >
              <i className="ri-external-link-line" /> {isKk ? 'Сайтқа өту' : 'На сайт'}
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-xs text-foreground-500 hover:text-red-400 font-label transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-line" /> {isKk ? 'Шығу' : 'Выйти'}
            </button>
          </div>
        </div>
      </header>

      <div className="w-full max-w-6xl mx-auto px-4 md:px-8 py-8">
        {/* Page title */}
        <div className="mb-8">
          <h1 className="font-heading text-foreground-100 text-2xl uppercase tracking-widest">
            {isKk ? 'Сайтты басқару' : 'Управление сайтом'}
          </h1>
          <p className="text-foreground-600 text-xs font-label tracking-wide mt-1">
            {isKk ? 'Құжаттарды және мәтіндік мазмұнды өңдеу' : 'Редактирование документов и текстового содержимого'}
          </p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { label: isKk ? 'Құжаттар' : 'Документов', count: docs.length, icon: 'ri-file-list-3-line' },
            { label: isKk ? 'Patriot құжаттары' : 'Документов Patriot', count: patriotDocs.length, icon: 'ri-shield-star-line' },
            { label: isKk ? 'Мәтіндік блоктар' : 'Текстовых блоков', count: 7, icon: 'ri-text' },
          ].map((stat) => (
            <div key={stat.label} className="bg-background-900/60 rounded-xl border border-background-700/40 px-5 py-4">
              <div className="flex items-center gap-3 mb-1">
                <span className="w-8 h-8 flex items-center justify-center text-accent-500">
                  <i className={`${stat.icon} text-lg`} />
                </span>
                <span className="font-heading text-2xl text-foreground-100">{stat.count}</span>
              </div>
              <p className="text-xs font-label text-foreground-500 tracking-wide">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-background-900/60 p-1 rounded-lg border border-background-800/40 w-fit">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-label tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                tab === t.key
                  ? 'bg-accent-500 text-background-950 font-bold'
                  : 'text-foreground-500 hover:text-foreground-300'
              }`}
            >
              <i className={t.icon} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div>
          {tab === 'docs' && (
            <DocEditor
              title={isKk ? 'Құжаттама' : 'Документация'}
              items={docs}
              storageKey={DOCS_KEY}
              onUpdate={setDocs}
            />
          )}
          {tab === 'patriot' && (
            <DocEditor
              title={isKk ? '«Patriot» құжаттамасы' : 'Документация «Patriot»'}
              items={patriotDocs}
              storageKey={PATRIOT_KEY}
              onUpdate={setPatriotDocs}
            />
          )}
          {tab === 'texts' && <TextEditor />}
        </div>
      </div>
    </div>
  );
}